import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TableContainer,
  Paper,
  Button,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import CategoryDropdown from "./CategoryDropdown";
import MyDatePicker from "./MyDatePicker";
import axiosInstance from "../api/axiosInstance";

const initTariff = {
  contract_from_date: null,
  contract_to_date: null,
  billing_type: "",
  vehicle_group_id: "",
  cust_days: "",
  cust_hours: "",
  cust_kms: "",
  cust_rate: "",
  cust_extra_hr_rate: "",
  cust_extra_km_rate: "",
  veh_days: "",
  veh_hours: "",
  veh_kms: "",
  veh_rate: "",
  veh_extra_hr_rate: "",
  veh_extra_km_rate: "",
};

const ContractTariffManager = forwardRef(
  ({ clientId, customerId, token }, ref) => {
    const [form, setForm] = useState(initTariff);
    const [tariffs, setTariffs] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [vehicleGroupOptions, setVehicleGroupOptions] = useState([]);

    // 🔥 Parent can read tariffs using ref.current.getTariffs()
    useImperativeHandle(ref, () => ({
      getTariffs: () => tariffs,
    }));

    // Load Vehicle Groups
    useEffect(() => {
      axiosInstance
        .get("/api/category", { params: { categorytype: "VehicleGroup" } })
        .then((res) => setVehicleGroupOptions(res.data || []))
        .catch(() => setVehicleGroupOptions([]));
    }, [clientId]);

    const vehicleGroupLabel = (id) => {
      const found = vehicleGroupOptions.find(
        (o) => String(o.categoryid) === String(id)
      );
      return found ? found.categoryname : id;
    };

    // Load saved tariffs from DB
    useEffect(() => {
      if (!clientId || !customerId) return;

      loadTariffs();
    }, [clientId, customerId]);

    const loadTariffs = async () => {
      try {
        const res = await axiosInstance.get("/api/tariff-contract", {
          params: { client_id: clientId, customer_id: customerId },
        });

        const rows = (res.data || []).map((r) => ({
          ...r,
          contract_from_date: r.contract_from_date
            ? new Date(r.contract_from_date)
            : null,
          contract_to_date: r.contract_to_date
            ? new Date(r.contract_to_date)
            : null,
        }));

        setTariffs(rows);
      } catch (err) {
        console.error("❌ Failed to load tariffs:", err);
        setTariffs([]);
      }
    };

    // Handlers
    const handleChange = (e) =>
      setForm({ ...form, [e.target.name]: e.target.value });

    const handleCategoryChange = (field, e) =>
      setForm({ ...form, [field]: e.target.value });

    const handleDateChange = (field, date) =>
      setForm({ ...form, [field]: date });

    const handleAdd = () => {
      if (!form.billing_type) return alert("Billing Type required");
      if (!form.vehicle_group_id) return alert("Vehicle Group required");

      const newRow = {
        ...form,
        contract_from_date: form.contract_from_date?.toISOString() || null,
        contract_to_date: form.contract_to_date?.toISOString() || null,
      };

      if (editIndex !== null) {
        const updated = [...tariffs];
        updated[editIndex] = newRow;
        setTariffs(updated);
        setEditIndex(null);
      } else {
        setTariffs([...tariffs, newRow]);
      }

      setForm(initTariff);
    };

    const handleEdit = (index) => {
      const row = tariffs[index];

      setForm({
        ...row,
        contract_from_date: row.contract_from_date
          ? new Date(row.contract_from_date)
          : null,
        contract_to_date: row.contract_to_date
          ? new Date(row.contract_to_date)
          : null,
      });

      setEditIndex(index);
    };

    const handleDelete = (index) => {
      if (!window.confirm("Delete this tariff?")) return;
      setTariffs(tariffs.filter((_, i) => i !== index));
    };

    const groupHeaderStyle = {
      background: "#f5f7f9",
      fontWeight: 700,
      border: "1px solid #d0d7de",
      fontSize: "0.75rem",
      padding: "4px",
      textAlign: "center",
      whiteSpace: "nowrap",
    };

    const subHeaderStyle = {
      background: "#fff",
      fontWeight: 600,
      border: "1px solid #d0d7de",
      fontSize: "0.70rem",
      padding: "4px",
      textAlign: "center",
      whiteSpace: "nowrap",
    };

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Monthly Contract Tariff
        </Typography>

        {/* Dates */}
        <Grid container spacing={2}>
          <Grid item md={3}>
            <MyDatePicker
              label="Contract From Date"
              value={form.contract_from_date}
              onChange={(d) => handleDateChange("contract_from_date", d)}
            />
          </Grid>

          <Grid item md={3}>
            <MyDatePicker
              label="Contract To Date"
              value={form.contract_to_date}
              onChange={(d) => handleDateChange("contract_to_date", d)}
            />
          </Grid>
        </Grid>

        {/* Billing Type + Vehicle Group */}
        <Grid container spacing={2} mt={1}>
          <Grid item md={5}>
            <TextField
              select
              fullWidth
              size="small"
              name="billing_type"
              label="Billing Type"
              value={form.billing_type}
              onChange={handleChange}
              sx={{ minWidth: 350 }}
            >
              <MenuItem value="">Select Billing Type</MenuItem>
              <MenuItem value="Monthly Km Limit Month Rate">
                Monthly Km Limit Month Rate
              </MenuItem>
              <MenuItem value="Monthly Hrs & Kms Limit Month Rate">
                Monthly Hrs & Kms Limit Month Rate
              </MenuItem>
              <MenuItem value="Daily Km Limit Month Rate">
                Daily Km Limit Month Rate
              </MenuItem>
              <MenuItem value="Daily Km Limit Daily Rate">
                Daily Km Limit Daily Rate
              </MenuItem>
              <MenuItem value="Kms Basis">Kms Basis</MenuItem>
              <MenuItem value="Per Person Basis">Per Person Basis</MenuItem>
            </TextField>
          </Grid>

          <Grid item md={4}>
            <Box sx={{ width: 350 }}>
              <CategoryDropdown
                clientId={clientId}
                token={token}
                categoryType="VehicleGroup"
                name="vehicle_group_id"
                label="Vehicle Group"
                value={String(form.vehicle_group_id || "")}
                onChange={(e) => handleCategoryChange("vehicle_group_id", e)}
                size="small"
                sx={{ width: "100%" }}
                SelectProps={{ sx: { width: "100%" } }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* CUSTOMER BILLING */}
        <Box mt={3}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Customer Billing
          </Typography>

          <Grid container spacing={1}>
            {[
              { name: "cust_days", label: "Days" },
              { name: "cust_hours", label: "Hours" },
              { name: "cust_kms", label: "KMs" },
              { name: "cust_rate", label: "Rate" },
              { name: "cust_extra_hr_rate", label: "Extra Hr Rate" },
              { name: "cust_extra_km_rate", label: "Extra KM Rate" },
            ].map((item) => (
              <Grid item md={2} key={item.name}>
                <TextField
                  size="small"
                  fullWidth
                  name={item.name}
                  label={item.label}
                  value={form[item.name]}
                  onChange={handleChange}
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* VEHICLE BILLING */}
        <Box mt={3}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Vehicle Billing
          </Typography>

          <Grid container spacing={1}>
            {[
              { name: "veh_days", label: "Days" },
              { name: "veh_hours", label: "Hours" },
              { name: "veh_kms", label: "KMs" },
              { name: "veh_rate", label: "Rate" },
              { name: "veh_extra_hr_rate", label: "Extra Hr Rate" },
              { name: "veh_extra_km_rate", label: "Extra KM Rate" },
            ].map((item) => (
              <Grid item md={2} key={item.name}>
                <TextField
                  size="small"
                  fullWidth
                  name={item.name}
                  label={item.label}
                  value={form[item.name]}
                  onChange={handleChange}
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            ))}

            {/* ADD BUTTON */}

            <Button
              variant="contained"
              onClick={handleAdd}
              sx={{ textTransform: "none" }}
            >
              {editIndex !== null ? "Update Tariff" : "+ Tariff"}
            </Button>
          </Grid>
        </Box>

        {/* -------------------- Tariff Table (with visible outer border) -------------------- */}
        <Box mt={3}>
          <TableContainer
            component={Paper}
            sx={{
              border: "1px solid #d0d7de", // visible outer border
              borderRadius: "6px",
              overflow: "hidden",
              boxShadow: "none",
            }}
          >
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",
                borderCollapse: "collapse", // make inner borders join outer border
                // ensure the table itself has a border so rounded corners look correct
                "&": {
                  border: "none",
                },

                // Header styles (applies to all th)
                "& thead th": {
                  backgroundColor: "#f3f4f6",
                  fontWeight: 600,
                  fontSize: "0.68rem", // reduced header font size
                  border: "1px solid #d0d7de", // column borders (visible)
                  padding: "6px 8px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                },

                // Body cell styles (applies to all td)
                "& tbody td": {
                  fontSize: "0.72rem", // body font size
                  border: "1px solid #e5e7eb", // inner cell borders
                  padding: "6px 8px",
                  whiteSpace: "nowrap",
                },

                // Hover
                "& tbody tr:hover td": {
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              <TableHead>
                {/* -------- Group Header Row -------- */}
                <TableRow>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ ...groupHeaderStyle, width: 180 }}
                  >
                    Billing Type
                  </TableCell>

                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ ...groupHeaderStyle, width: 100 }}
                  >
                    Vehicle Group
                  </TableCell>

                  <TableCell align="center" colSpan={6} sx={groupHeaderStyle}>
                    Customer Billing
                  </TableCell>

                  <TableCell align="center" colSpan={6} sx={groupHeaderStyle}>
                    Vehicle Billing
                  </TableCell>

                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{ ...groupHeaderStyle, width: 80 }}
                  >
                    Actions
                  </TableCell>
                </TableRow>

                {/* -------- Column Header Row -------- */}
                <TableRow>
                  <TableCell sx={{ ...subHeaderStyle, width: 50 }}>
                    Days
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 50 }}>
                    Hours
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 60 }}>
                    KMs
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 100 }}>
                    Rate
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 90 }}>
                    Hr Rate
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 90 }}>
                    Km Rate
                  </TableCell>

                  <TableCell sx={{ ...subHeaderStyle, width: 50 }}>
                    Days
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 50 }}>
                    Hours
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 60 }}>
                    KMs
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 100 }}>
                    Rate
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 90 }}>
                    Hr Rate
                  </TableCell>
                  <TableCell sx={{ ...subHeaderStyle, width: 90 }}>
                    Km Rate
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody
                sx={{
                  "& td": {
                    fontSize: "0.72rem",
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                    border: "1px solid #e5e7eb",
                  },
                  "& tr:hover td": {
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                {tariffs.length > 0 ? (
                  tariffs.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ width: 180 }}>
                        {t.billing_type}
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        {vehicleGroupLabel(t.vehicle_group_id)}
                      </TableCell>

                      {/* Customer Billing */}
                      <TableCell sx={{ width: 60 }} align="center">
                        {t.cust_days}
                      </TableCell>
                      <TableCell sx={{ width: 60 }} align="center">
                        {t.cust_hours}
                      </TableCell>
                      <TableCell sx={{ width: 70 }} align="center">
                        {t.cust_kms}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.cust_rate}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.cust_extra_hr_rate}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.cust_extra_km_rate}
                      </TableCell>

                      {/* Vehicle Billing */}
                      <TableCell sx={{ width: 60 }} align="center">
                        {t.veh_days}
                      </TableCell>
                      <TableCell sx={{ width: 60 }} align="center">
                        {t.veh_hours}
                      </TableCell>
                      <TableCell sx={{ width: 70 }} align="center">
                        {t.veh_kms}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.veh_rate}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.veh_extra_hr_rate}
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="right">
                        {t.veh_extra_km_rate}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ width: 80 }} align="center">
                        <IconButton size="small" onClick={() => handleEdit(i)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(i)}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 2 }}>
                      No tariff records available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }
);

export default ContractTariffManager;
